import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import {
	ICmmsFilesQueryVariables,
	IFileEntity,
} from '@app/pages/cmms/interfaces/files.interfaces';
import {
	addFile,
	addFiles,
	deleteFile,
	deleteFiles,
	dropFilesQueryVariables,
	filesLoaded,
	mapFile,
	mapFiles,
	replacingFileStarts,
	setDetectionTask,
	setFile,
	setFileForEditing,
	setFiles,
	setFilesTableConfig,
	startLoadingFiles,
	storeFilesQueryVariables,
	updateActiveTab,
	updateDetectionTask,
	updateFile,
	updateFiles,
	upsertFile,
	upsertFiles,
} from '@app/pages/cmms/components/files/state/files.action';

import {
	IAuPaginationData,
	IDjangoPaginationData,
} from '@shared/ui-components/au-paginator/pagination-page.info';
import { ICMMSColumn } from '@shared/ui-components/au-table-layout/utils/interfaces';
import { DocumentNode } from 'graphql/index';
import {
	DetectionTaskStatus,
	IDetectionTask,
	IResultData,
} from '@app/pages/cmms/interfaces/incident-detection.interfaces';

export interface IFileState extends EntityState<IFileEntity> {
	filesAuPagination: IAuPaginationData | null;
	filesPagination: IDjangoPaginationData | null;
	filesQueryVariables: ICmmsFilesQueryVariables | null;
	fileEdit: IFileEntity;
	isLoadingFiles: boolean;
	replacingInProgress: boolean;
	isExportable: boolean;
	tableColumnsConfig: ICMMSColumn[] | null;
	exportUrlQuery: DocumentNode | null;
	detectionTaskResults: {
		[fileId: string]: IDetectionTask & { status: DetectionTaskStatus };
	};
	fileActiveTab: { [fileId: string]: 'view' | 'info' };
}

export function selectFileId(fileEntity: IFileEntity): string {
	return fileEntity.pk;
}

export const FileAdapter: EntityAdapter<IFileEntity> = createEntityAdapter<IFileEntity>({
	selectId: selectFileId,
});

export const initialState: IFileState = FileAdapter.getInitialState({
	// additional entity state properties
	filesAuPagination: null,
	filesPagination: null,
	filesQueryVariables: null,
	fileEdit: null,
	isLoadingFiles: true,
	replacingInProgress: false,
	isExportable: false,
	tableColumnsConfig: null,
	exportUrlQuery: null, //add here the query for export to unlock it
	detectionTaskResults: {},
	fileActiveTab: {},
});

export const fileReducer = createReducer(
	initialState,
	on(addFile, (state, { file }) => {
		return FileAdapter.addOne(file, state);
	}),
	on(setFile, (state, { file }) => {
		return FileAdapter.setOne(file, state);
	}),
	on(upsertFile, (state, { file }) => {
		return FileAdapter.upsertOne(file, state);
	}),
	on(addFiles, (state, { files }) => {
		return FileAdapter.addMany(files, state);
	}),
	on(upsertFiles, (state, { files }) => {
		return FileAdapter.upsertMany(files, state);
	}),
	on(updateFile, (state, { update }) => {
		return FileAdapter.updateOne(update, {
			...state,
			replacingInProgress: false,
			fileEdit: update.changes as IFileEntity,
		});
	}),
	on(updateFiles, (state, { updates }) => {
		return FileAdapter.updateMany(updates, state);
	}),
	on(mapFile, (state, { entityMap }) => {
		return FileAdapter.mapOne(entityMap, state);
	}),
	on(mapFiles, (state, { entityMap }) => {
		return FileAdapter.map(entityMap, state);
	}),
	on(deleteFile, (state, { pk }) => {
		return FileAdapter.removeOne(pk, state);
	}),
	on(deleteFiles, (state, { pk }) => {
		return FileAdapter.removeMany(pk, state);
	}),
	on(startLoadingFiles, (state, { variables }) => {
		return {
			...state,
			isLoadingFiles: true,
			filesAuPagination: {
				first: variables?.first,
				last: variables?.last,
				after: variables?.after,
				before: variables?.before,
			},
		};
	}),
	on(filesLoaded, (state, { files, filesPagination }) => {
		return FileAdapter.setAll(files, {
			...state,
			filesPagination: filesPagination,
			isLoadingFiles: false,
		});
	}),
	on(setFileForEditing, (state, { fileEdit }) => {
		return { ...state, fileEdit };
	}),
	on(setFiles, (state, { files }) => {
		return FileAdapter.setMany(files, state);
	}),
	on(replacingFileStarts, state => {
		return { ...state, replacingInProgress: true };
	}),
	on(storeFilesQueryVariables, (state, { variables }) => {
		return { ...state, filesQueryVariables: variables };
	}),
	on(dropFilesQueryVariables, state => {
		return { ...state, filesQueryVariables: null };
	}),
	on(setFilesTableConfig, (state, { isExportable, columns }) => {
		return {
			...state,
			isExportable,
			tableColumnsConfig: columns,
		};
	}),
	on(updateDetectionTask, (state, { updateVariables }) => {
		const detectionTaskResults = state.detectionTaskResults;
		const results = detectionTaskResults[updateVariables.fileId];

		const { deviation: parsedResults } = JSON.parse(results.results) as {
			deviation?: IResultData[];
		};
		if (!parsedResults) return state;
		const indx = parsedResults.findIndex(
			result => result.ID === updateVariables.resultId
		);
		parsedResults[indx] = {
			...parsedResults[indx],
			...updateVariables.updateValues,
		};
		const updatedResults = JSON.stringify({ deviation: parsedResults });

		return {
			...state,
			detectionTaskResults: {
				...detectionTaskResults,
				[updateVariables.fileId]: {
					...results,
					results: updatedResults,
				},
			},
		};
	}),
	on(setDetectionTask, (state, { result, detectionTaskStatus }) => {
		const results = state.detectionTaskResults;
		const resultWithStatus = {
			status: detectionTaskStatus,
			...result,
		};
		return {
			...state,
			detectionTaskResults: { ...results, [resultWithStatus.fileId]: resultWithStatus },
		};
	}),
	on(updateActiveTab, (state, { activeTab, fileId }) => {
		const fileTab = { [fileId]: activeTab };
		return {
			...state,
			fileActiveTab: fileTab,
		};
	})
);

const { selectIds, selectEntities, selectAll, selectTotal } = FileAdapter.getSelectors();

export const FileEntitySelector = {
	selectIds,
	selectEntities,
	selectAll,
	selectTotal,
};
